import { useState, useEffect } from 'react';
import type { Site, Page, Component, PageLayout } from '../schemas/validation';
import { getPublicApiBaseUrl } from '../utils/publicApiBaseUrl';

interface WebsiteData {
  website: Site | null;
  page: Page | null;
  components: Component[];
  navigation: Array<{ title: string; slug: string; orderPosition: number }>;
  theme: any;
  layout: PageLayout | null;
  loading: boolean;
  error: string | null;
  /**
   * Whether to show the "Powered by" branding badge.
   * True for FREE tier users, false for paid plans.
   */
  showBranding: boolean;
}

export const useWebsiteData = (
  identifier: string,
  slug: string = '',
  isCustomDomain: boolean = false
): WebsiteData => {
  const [data, setData] = useState<WebsiteData>({
    website: null,
    page: null,
    components: [],
    navigation: [],
    theme: null,
    layout: null,
    loading: true,
    error: null,
    showBranding: false,
  });

  useEffect(() => {
    const fetchWebsiteData = async () => {
      if (!identifier) return;

      try {
        setData(prev => ({ ...prev, loading: true, error: null }));

        const apiBaseUrl = getPublicApiBaseUrl();

        console.log('🔍 Fetching website data:', {
          identifier,
          slug,
          isCustomDomain,
          apiBaseUrl: apiBaseUrl || 'relative path',
          hostname: window.location.hostname,
        });

        if (slug) {
          // Fetch specific page data using appropriate endpoint
          const pageUrl = isCustomDomain
            ? `${apiBaseUrl}/api/website/by-domain/${identifier}/page/${slug}`
            : `${apiBaseUrl}/api/website/${identifier}/page/${slug}`;
          console.log('📄 Fetching page data from:', pageUrl);

          const pageResponse = await fetch(pageUrl);

          if (!pageResponse.ok) {
            if (pageResponse.status === 404) {
              // Page not found, fall back to homepage
              console.warn(`Page "${slug}" not found, falling back to homepage`);
              const websiteUrl = isCustomDomain
                ? `${apiBaseUrl}/api/website/by-domain/${identifier}`
                : `${apiBaseUrl}/api/website/${identifier}`;
              const websiteResponse = await fetch(websiteUrl);

              if (!websiteResponse.ok) {
                throw new Error(
                  `Website not found: ${identifier} (Status: ${websiteResponse.status})`
                );
              }

              const websiteResponse_data = await websiteResponse.json();
              console.log('🌐 Website data received:', websiteResponse_data);

              // Extract data from the response structure
              const websiteData = websiteResponse_data.success
                ? websiteResponse_data.data
                : websiteResponse_data;

              // Find homepage from the website data
              const homepage =
                websiteData.pages?.find(
                  (p: any) => p.isHomePage || p.slug === '' || p.slug === 'home'
                ) || websiteData.pages?.[0];

              // Parse components if they're stored as JSON string
              let components = [];
              if (homepage?.components) {
                try {
                  const parsedComponents =
                    typeof homepage.components === 'string'
                      ? JSON.parse(homepage.components)
                      : homepage.components;
                  components = parsedComponents.components || parsedComponents || [];
                } catch (error) {
                  console.error('Error parsing homepage components:', error);
                  components = [];
                }
              }

              // Parse layout if present
              let layout: PageLayout | null = null;
              if (websiteData.layout) {
                layout = websiteData.layout;
                if (layout && layout.components && typeof layout.components === 'string') {
                  try {
                    const parsedLayoutComponents = JSON.parse(layout.components);
                    layout = {
                      ...layout,
                      components: parsedLayoutComponents.components || parsedLayoutComponents || [],
                    };
                  } catch (e) {
                    console.error('Error parsing layout components:', e);
                  }
                }
              }

              setData({
                website: websiteData.site,
                page: homepage || null,
                components: components,
                navigation: websiteData.navigation || [],
                theme: websiteData.theme || null,
                layout: layout,
                loading: false,
                error: null,
                showBranding: websiteData.showBranding ?? false,
              });
              return;
            } else {
              throw new Error(
                `Failed to fetch page: ${pageResponse.status} ${pageResponse.statusText}`
              );
            }
          }

          const pageResponse_data = await pageResponse.json();
          console.log('📄 Page data received:', pageResponse_data);

          // Extract data from the response structure
          const pageData = pageResponse_data.success ? pageResponse_data.data : pageResponse_data;

          // Parse components if they're stored as JSON string
          let components = [];
          if (pageData.page?.components) {
            try {
              const parsedComponents =
                typeof pageData.page.components === 'string'
                  ? JSON.parse(pageData.page.components)
                  : pageData.page.components;
              components = parsedComponents.components || parsedComponents || [];
            } catch (error) {
              console.error('Error parsing page components:', error);
              components = [];
            }
          }

          // Parse layout if present
          let layout: PageLayout | null = null;
          if (pageData.layout) {
            layout = pageData.layout;
            if (layout && layout.components && typeof layout.components === 'string') {
              try {
                const parsedLayoutComponents = JSON.parse(layout.components);
                layout = {
                  ...layout,
                  components: parsedLayoutComponents.components || parsedLayoutComponents || [],
                };
              } catch (e) {
                console.error('Error parsing layout components:', e);
              }
            }
          }
          console.log(
            '📐 useWebsiteData: Layout data:',
            layout ? { name: layout.name, componentsCount: layout.components?.length } : 'No layout'
          );

          setData({
            website: pageData.site,
            page: pageData.page,
            components: components,
            navigation: pageData.navigation || [],
            theme: pageData.theme || null,
            layout: layout,
            loading: false,
            error: null,
            showBranding: pageData.showBranding ?? false,
          });
        } else {
          // Fetch complete website data using appropriate endpoint
          const websiteUrl = isCustomDomain
            ? `${apiBaseUrl}/api/website/by-domain/${identifier}`
            : `${apiBaseUrl}/api/website/${identifier}`;
          console.log('🌐 Fetching website data from:', websiteUrl);

          const websiteResponse = await fetch(websiteUrl);

          if (!websiteResponse.ok) {
            throw new Error(`Website not found: ${identifier} (Status: ${websiteResponse.status})`);
          }

          const websiteResponse_data = await websiteResponse.json();
          console.log('🌐 Website data received:', websiteResponse_data);

          // Extract data from the response structure
          const websiteData = websiteResponse_data.success
            ? websiteResponse_data.data
            : websiteResponse_data;

          // Find homepage from the website data
          const homepage =
            websiteData.pages?.find(
              (p: any) => p.isHomePage || p.slug === '' || p.slug === 'home'
            ) || websiteData.pages?.[0];

          // Parse components if they're stored as JSON string
          let components = [];
          if (homepage?.components) {
            try {
              const parsedComponents =
                typeof homepage.components === 'string'
                  ? JSON.parse(homepage.components)
                  : homepage.components;
              components = parsedComponents.components || parsedComponents || [];

              // Debug logging for SEO component
              const seoComponent = components.find((c: any) => c.type === 'head-content');
              if (seoComponent) {
                console.log('🔍 SEO Component found in parsed data:', {
                  id: seoComponent.id,
                  type: seoComponent.type,
                  hasData: !!seoComponent.data,
                  dataKeys: Object.keys(seoComponent.data || {}),
                  dataValues: seoComponent.data,
                  title: seoComponent.data?.title,
                  description: seoComponent.data?.description,
                  keywords: seoComponent.data?.keywords,
                  canonicalUrl: seoComponent.data?.canonicalUrl,
                  favicon: seoComponent.data?.favicon,
                });
              }
            } catch (error) {
              console.error('Error parsing homepage components:', error);
              components = [];
            }
          }

          // Parse layout if present
          let layout: PageLayout | null = null;
          if (websiteData.layout) {
            layout = websiteData.layout;
            if (layout && layout.components && typeof layout.components === 'string') {
              try {
                const parsedLayoutComponents = JSON.parse(layout.components);
                layout = {
                  ...layout,
                  components: parsedLayoutComponents.components || parsedLayoutComponents || [],
                };
              } catch (e) {
                console.error('Error parsing layout components:', e);
              }
            }
          }
          console.log(
            '📐 useWebsiteData (homepage): Layout data:',
            layout ? { name: layout.name, componentsCount: layout.components?.length } : 'No layout'
          );

          setData({
            website: websiteData.site,
            page: homepage || null,
            components: components,
            navigation: websiteData.navigation || [],
            theme: websiteData.theme || null,
            layout: layout,
            loading: false,
            error: null,
            showBranding: websiteData.showBranding ?? false,
          });
        }
      } catch (error) {
        console.error('❌ Error fetching website data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch',
        }));
      }
    };

    fetchWebsiteData();
  }, [identifier, slug, isCustomDomain]);

  return data;
};
